import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Lock, Unlock } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

import ClubbieModal from '../Modal/ClubbieModal';
import CategoryTabs from '../../pages/tournament/format/components/CategoryTabs';
import { bulkLockPhases } from '../../redux/slices/tournamentSlice';

const formatRange = (start, end) => {
  if (!start || !end) return '';
  const s = format(new Date(start), 'dd.MM.yyyy');
  const e = format(new Date(end), 'dd.MM.yyyy');
  return s === e ? s : `${s} - ${e}`;
};

const LockStagesModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { selectedTournament } = useSelector((state) => state.tournament);

  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const categories = useMemo(
    () => selectedTournament?.categories || [],
    [selectedTournament]
  );

  const [activeCategoryLabel, setActiveCategoryLabel] = useState('');
  // phaseId → boolean (intended locked state). Only diffs against on-disk are kept.
  const [pendingLocks, setPendingLocks] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Reset whenever the modal opens or the underlying tournament changes
  useEffect(() => {
    if (!isOpen) return;
    setPendingLocks({});
    setActiveCategoryLabel(categories[0]?.label || '');
  }, [isOpen, tournamentId, categories]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.label === activeCategoryLabel) || null,
    [categories, activeCategoryLabel]
  );
  const phases = activeCategory?.phases || [];

  const phaseLocked = (phase) => {
    const id = phase._id || phase.id;
    return pendingLocks[id] !== undefined ? pendingLocks[id] : !!phase.locked;
  };

  const togglePhase = (phase) => {
    const id = phase._id || phase.id;
    const onDisk = !!phase.locked;
    const currentIntended = pendingLocks[id] !== undefined ? pendingLocks[id] : onDisk;
    const nextIntended = !currentIntended;
    setPendingLocks((prev) => {
      const next = { ...prev };
      if (nextIntended === onDisk) {
        delete next[id];
      } else {
        next[id] = nextIntended;
      }
      return next;
    });
  };

  const hasChanges = Object.keys(pendingLocks).length > 0;

  const handleSave = async () => {
    if (!hasChanges) {
      onClose?.();
      return;
    }

    const changes = [];
    for (const cat of categories) {
      const catId = cat._id || cat.id;
      for (const phase of cat.phases || []) {
        const phaseId = phase._id || phase.id;
        const intended = pendingLocks[phaseId];
        if (intended !== undefined && intended !== !!phase.locked) {
          changes.push({ categoryId: catId, phaseId, locked: intended });
        }
      }
    }
    if (changes.length === 0) {
      onClose?.();
      return;
    }

    setIsSaving(true);
    const loadingToast = toast.loading('Saving changes...');
    try {
      await dispatch(
        bulkLockPhases({ tournamentId, phases: changes })
      ).unwrap();
      toast.dismiss(loadingToast);
      toast.success('Stages updated');
      onClose?.();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.message || 'Failed to update stages');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setPendingLocks({});
    onClose?.();
  };

  return (
    <ClubbieModal
      isOpen={isOpen}
      toggle={handleClose}
      title="Lock stages"
      width="600px"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="px-6 py-3 rounded-full bg-[#00E7FF] hover:opacity-50 text-[#021529] text-sm font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {isSaving ? 'Saving...' : 'Save changes'}
          </button>
        </>
      }
    >
      <p className="text-app-text-secondary text-[13px] mb-4">
        Locked stages will prevent other users from adding or changing match results.
      </p>

      {categories.length > 1 && (
        <div className="mb-4">
          <CategoryTabs
            categories={categories}
            selectedCategory={activeCategoryLabel}
            onCategoryChange={setActiveCategoryLabel}
          />
        </div>
      )}

      {phases.length === 0 ? (
        <div className="py-8 text-center text-app-text-secondary text-sm">
          No format found.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {phases.map((phase) => {
            const id = phase._id || phase.id;
            const locked = phaseLocked(phase);
            const range = formatRange(
              phase.matchSchedule?.startDate,
              phase.matchSchedule?.endDate
            );
            return (
              <div key={id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => togglePhase(phase)}
                  disabled={isSaving}
                  className="flex items-center justify-center gap-1.5 h-10 min-w-[85px] px-4 bg-[#082441] hover:bg-[#0a2d52] text-[#FEFCEB] rounded-full border border-[#192634] transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {locked ? <Unlock size={14} /> : <Lock size={14} />}
                  <span className="text-sm font-medium">{locked ? 'Unlock' : 'Lock'}</span>
                </button>
                <div className="flex-1 min-w-0 text-app-text text-[14px]">
                  <span className="font-medium">{phase.name}</span>
                  {range && (
                    <span className="text-app-text-secondary"> ({range})</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ClubbieModal>
  );
};

export default LockStagesModal;
